'use client';

import { useTranslation } from '@/app/i18n/client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form, FormDescription } from '@/components/ui/form';
import { OTPValidatorSchema, generateOTPSchema } from '@/lib/validators/otp';
import { hideMobileNumberDigit } from '@/util/util';
import React, { useRef, useState } from 'react';
import { CountdownTimer } from '@/components/countdown-timer';
import {
    sendOTPSignup,
    sendOTPSignupPhone,
    verifyOTPSignup,
    verifyOTPSignupPhone,
} from '@/actions';
import toast from 'react-hot-toast';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import { Loader2 } from 'lucide-react';
import { useContext } from 'react';
import OTPInputGrid from './otp-input-grid';

interface otpResponseData {
    ref: string;
    transId: string;
    refCode: string;
}
interface OTPFormProps {
    numDigits?: number;
    type: 'email' | 'mobile';
    value: string;
    data: otpResponseData;
    onOTPSubmit?: (success: boolean) => void;
    radiusStyle: any;
}

const OTPFormSignup: React.FC<OTPFormProps> = ({
    numDigits = 6,
    type,
    value,
    data,
    onOTPSubmit,
    radiusStyle,
}) => {
    const { t } = useTranslation();

    const [otp, setOtp] = useState<string[]>(new Array(numDigits).fill(''));
    const inpRefs = useRef(null);
    const [activeBtnSubmit, setActiveBtnSubmit] = useState(false);
    const [activeBtnResendOTP, setActiveBtnResendOTP] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResendingOTP, setIsResendingOTP] = useState<boolean>(false);
    const form = useForm<OTPValidatorSchema>({
        resolver: zodResolver(generateOTPSchema(numDigits)),
    });

    const [otpResponseData, setOtpResponseData] = useState({
        ref: data.ref,
        transId: data.transId,
        refCode: data.refCode,
    });

    const [timerValue, setTimerValue] = useState(60);

    const handleTimerUpdate = (currentTime: number) => {
        if (currentTime === 0) setActiveBtnResendOTP(true);
        setTimerValue(currentTime);
    };

    const onSubmit = (values: OTPValidatorSchema) => {
        setIsLoading(true);
        const otpValue = Object.values(values).join('');
        const verifyFn =
            type === 'mobile' ? verifyOTPSignupPhone : verifyOTPSignup;
        verifyFn(
            otpResponseData.ref,
            otpValue,
            otpResponseData.transId,
            value,
            otpResponseData.refCode,
        )
            .then((res: any) => {
                if (res && res.result.success == true) {
                    setIsLoading(false);
                    if (onOTPSubmit) onOTPSubmit(true);
                } else {
                    setIsLoading(false);
                    toast.error(`${t('signup.otp.otp_expired')}`, {
                        position: 'bottom-center',
                        style: {
                            zIndex: 999999,
                        },
                    });
                }
            })
            .catch((error: any) => {
                toast.error(`${t('signup.otp.toast_failed_message')}`, {
                    position: 'bottom-center',
                    duration: 3000,
                    style: {
                        zIndex: 999999,
                    },
                });
            })
            .finally(() => {
                setTimeout(() => {
                    setIsLoading(false);
                }, 3000);
            });
    };

    const onSubmitResendOTP = () => {
        setIsResendingOTP(true);
        const verifyFn = type === 'mobile' ? sendOTPSignupPhone : sendOTPSignup;
        verifyFn(value)
            .then((res) => {
                if (
                    res.statusCode === 429 ||
                    res.error?.[0] === 'OTP_REQUEST_RATE_LIMITED'
                ) {
                    const errorKey =
                        type === 'mobile'
                            ? 'signup.otp.phone_otp_rate_limited'
                            : 'signup.OTP_REQUEST_RATE_LIMITED';
                    toast.error(`${t(errorKey as any)}`, {
                        position: 'bottom-center',
                        duration: 3000,
                        style: {
                            zIndex: 999999,
                        },
                    });
                    return;
                }

                if (res?.result) {
                    setOtpResponseData({
                        ref: res.result.ref,
                        transId: res.result.transId,
                        refCode: res.result.refCode,
                    });
                    setTimerValue(60);
                    setActiveBtnResendOTP(false);
                    toast.success(t('signup.otp.resend_otp_success'), {
                        position: 'bottom-center',
                        duration: 2000,
                        style: {
                            zIndex: 999999,
                        },
                    });
                }
            })
            .catch((error: any) => {
                const errorMessage =
                    type === 'mobile'
                        ? `${t('signup.otp.phone_already_verified')}`
                        : `${t('signup.otp.email_already_verified')}`;
                toast.error(errorMessage, {
                    position: 'bottom-center',
                    duration: 3000,
                    style: {
                        zIndex: 999999,
                    },
                });
            })
            .finally(() => {
                setIsResendingOTP(false);
            });
    };

    const colorStyle = useContext(ColorStyleContext);

    return (
        <>
            <div className="flex items-center justify-center">
                <span
                    className="font-normal text-xl"
                    style={{ color: colorStyle?.text || '#3d3d3d' }}
                >
                    {t('signup.otp.title')}
                </span>
            </div>
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    ref={inpRefs}
                    className="space-y-4 p-6"
                >
                    <FormDescription
                        className="font-light text-sm"
                        style={{
                            color: colorStyle?.text || '#3d3d3d',
                        }}
                    >
                        {type == 'mobile'
                            ? t('signup.otp.text_sent_mobile_otp', {
                                  mobile: `${hideMobileNumberDigit(value)}`,
                              })
                            : t('signup.otp.text_sent_email_otp', {
                                  email: `${hideMobileNumberDigit(value)}`,
                              })}
                    </FormDescription>
                    <OTPInputGrid
                        form={form}
                        numDigits={numDigits}
                        otp={otp}
                        setOtp={setOtp}
                        inpRefs={inpRefs}
                        setActiveBtnSubmit={setActiveBtnSubmit}
                    />
                    <div>
                        <FormDescription
                            className="font-light text-sm opacity-50"
                            style={{
                                color: colorStyle?.text || '#3d3d3d',
                            }}
                        >
                            {t('signup.ref_code')} {otpResponseData.refCode}
                        </FormDescription>
                    </div>
                    <div className="w-full flex flex-col md:flex-row items-center justify-between gap-5">
                        <button
                            disabled={!activeBtnSubmit || isLoading}
                            type="submit"
                            className={`h-[48px] w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-normal ${
                                activeBtnSubmit ? 'cursor-pointer' : ''
                            } whitespace-nowrap px-4 py-2`}
                            style={
                                {
                                    '--dynamic-bg': activeBtnSubmit
                                        ? colorStyle?.buttonBgNormal
                                        : '#f8fafc',
                                    '--dynamic-hover-bg': activeBtnSubmit
                                        ? colorStyle?.buttonBgHover
                                        : '#f8fafc',
                                    '--dynamic-color': activeBtnSubmit
                                        ? colorStyle?.buttonTextNormal
                                        : '#9397a0',
                                    '--dynamic-hover-color': activeBtnSubmit
                                        ? colorStyle?.buttonTextHover
                                        : '#9397a0',
                                    '--dynamic-focus-color': activeBtnSubmit
                                        ? colorStyle?.buttonTextHover
                                        : '#9397a0',
                                    borderTopLeftRadius:
                                        radiusStyle?.buttonRadiusTopLeft ??
                                        '6px',
                                    borderTopRightRadius:
                                        radiusStyle?.buttonRadiusTopRight ??
                                        '6px',
                                    borderBottomLeftRadius:
                                        radiusStyle?.buttonRadiusBottomLeft ??
                                        '6px',
                                    borderBottomRightRadius:
                                        radiusStyle?.buttonRadiusBottomRight ??
                                        '6px',
                                } as React.CSSProperties
                            }
                        >
                            <div className="flex items-center justify-center gap-2">
                                {isLoading && (
                                    <Loader2 className="animate-spin w-4 h-4" />
                                )}
                                {t('signup.otp.btn_confirm_otp')}
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={onSubmitResendOTP}
                            disabled={!activeBtnResendOTP || isResendingOTP}
                            className={`h-[48px] w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-normal ${
                                activeBtnResendOTP && !isResendingOTP
                                    ? 'cursor-pointer'
                                    : 'opacity-50'
                            } whitespace-nowrap px-4 py-2`}
                            style={
                                {
                                    '--dynamic-bg':
                                        activeBtnResendOTP && !isResendingOTP
                                            ? colorStyle?.buttonBgNormal
                                            : '#f8fafc',
                                    '--dynamic-hover-bg':
                                        activeBtnResendOTP && !isResendingOTP
                                            ? colorStyle?.buttonBgHover
                                            : '#f8fafc',
                                    '--dynamic-color':
                                        activeBtnResendOTP && !isResendingOTP
                                            ? colorStyle?.buttonTextNormal
                                            : '#9397a0',
                                    '--dynamic-hover-color':
                                        activeBtnResendOTP && !isResendingOTP
                                            ? colorStyle?.buttonTextHover
                                            : '#9397a0',
                                    '--dynamic-focus-color':
                                        activeBtnResendOTP && !isResendingOTP
                                            ? colorStyle?.buttonTextHover
                                            : '#9397a0',
                                    borderTopLeftRadius:
                                        radiusStyle?.buttonRadiusTopLeft ??
                                        '6px',
                                    borderTopRightRadius:
                                        radiusStyle?.buttonRadiusTopRight ??
                                        '6px',
                                    borderBottomLeftRadius:
                                        radiusStyle?.buttonRadiusBottomLeft ??
                                        '6px',
                                    borderBottomRightRadius:
                                        radiusStyle?.buttonRadiusBottomRight ??
                                        '6px',
                                } as React.CSSProperties
                            }
                        >
                            <div className="flex items-center justify-center gap-2">
                                {isResendingOTP && (
                                    <Loader2 className="animate-spin w-4 h-4" />
                                )}
                                {activeBtnResendOTP ? (
                                    <>{t('signup.otp.btn_resend_otp')}</>
                                ) : (
                                    <>
                                        {t('signup.otp.btn_not_receive_otp')}{' '}
                                        {timerValue > 0 && (
                                            <CountdownTimer
                                                duration={timerValue}
                                                onTimerUpdate={
                                                    handleTimerUpdate
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </button>
                    </div>
                </form>
            </Form>
        </>
    );
};

export default OTPFormSignup;
